/**
 * @fileoverview Página responsável por administrar conexões do WhatsApp e seus estados.
 */

import React, { useState, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, red, orange } from "@material-ui/core/colors";
import {
	Button,
	IconButton,
	Paper,
	Typography,
	CircularProgress,
	Grid,
	Card,
	CardContent,
	CardActions,
	Chip,
	Box,
	Tooltip,
} from "@material-ui/core";
	import {
		Play as LucidePlay,
		Square as LucideStop,
		RotateCcw as LucideRotateCcw,
		Pencil,
		Plus,
		Trash2 as Trash
	} from "lucide-react";
import { FaWhatsapp, FaWhatsappSquare } from "react-icons/fa";

import MainContainer from "../../components/MainContainer";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(3),
		flexWrap: 'wrap',
		gap: theme.spacing(2),
	},
	title: {
		fontSize: "1.75rem",
		fontWeight: 700,
		color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
	},
	buttonsWrapper: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		flexWrap: 'wrap',
	},
	addButton: {
		whiteSpace: 'nowrap',
		flexShrink: 0,
		height: 36,
		padding: '0 14px',
		borderRadius: 10,
		fontSize: '0.875rem',
		fontWeight: 500,
		textTransform: 'none',
		gap: 8,
		border: 'none',
		boxShadow: 'none',
		background: 'linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)',
		color: '#ffffff',
		transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
		'&:hover': {
			background: 'linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)',
			boxShadow: 'none',
		},
		'& svg': { flexShrink: 0 },
	},
	mainPaper: {
		flex: 1,
		padding: theme.spacing(2),
		paddingTop: theme.spacing(2),
		overflowY: "scroll",
		backgroundColor: "transparent",
		boxShadow: "none",
		...theme.scrollbarStyles,
	},
	pageContainer: {
		backgroundColor: theme.palette.type === "light" ? "#f8fafc" : "#0A0A0A",
		minHeight: "100vh",
		padding: 0,
	},
	subtitle: {
		fontSize: "0.95rem",
		color: theme.palette.type === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.6)',
	},
	cardsContainer: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
		gap: theme.spacing(3),
		marginTop: theme.spacing(3),
		width: '100%',
	},
	card: {
		background: theme.palette.type === 'dark'
			? 'linear-gradient(145deg, rgba(17,24,39,0.95) 0%, rgba(23,37,84,0.9) 45%, rgba(59,130,246,0.08) 100%)'
			: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
		border: '1px solid rgba(99, 102, 241, 0.2)',
		borderRadius: "18px",
		boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
		transition: "transform 0.2s ease, box-shadow 0.2s ease",
		overflow: 'visible',
		position: 'relative',
		fontFamily: '"Montserrat", sans-serif',
		"&:hover": {
			transform: 'translateY(-3px)',
			boxShadow: '0 16px 38px rgba(79, 70, 229, 0.22)',
		},
	},
	cardHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		marginBottom: theme.spacing(2),
	},
	connectionName: {
		fontSize: '1.3rem',
		fontWeight: 700,
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, #FFFFFF 0%, rgba(255, 255, 255, 0.8) 100%)'
			: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent',
		backgroundClip: 'text',
		marginBottom: theme.spacing(0.5),
	},
	connectionNumber: {
		marginTop: theme.spacing(1),
		fontSize: '0.9rem',
		color: theme.palette.type === 'light' ? '#64748b' : 'rgba(255, 255, 255, 0.55)',
		fontFamily: '"Montserrat", sans-serif',
		fontWeight: 500,
		letterSpacing: '0.6px',
	},
	statusText: {
		fontSize: '0.9rem',
		fontWeight: 600,
		color: '#cbd5e1',
		marginTop: theme.spacing(1),
	},
	cardActions: {
		display: 'flex',
		gap: theme.spacing(1),
	},
	statusChip: {
		fontWeight: 600,
		fontSize: '0.75rem',
		height: '26px',
		borderRadius: '8px',
	},
	statusConnected: {
		background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(21, 128, 61, 0.15) 100%)',
		color: theme.palette.type === 'dark' ? '#86efac' : '#15803d',
		border: theme.palette.type === 'dark' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(34, 197, 94, 0.2)',
		boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
	},
	statusDisconnected: {
		background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(185, 28, 28, 0.15) 100%)',
		color: theme.palette.type === 'dark' ? '#fca5a5' : '#b91c1c',
		border: theme.palette.type === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
		boxShadow: '0 2px 8px rgba(239, 68, 68, 0.15)',
	},
	statusConnecting: {
		background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(194, 65, 12, 0.15) 100%)',
		color: theme.palette.type === 'dark' ? '#fdba74' : '#c2410c',
		border: theme.palette.type === 'dark' ? '1px solid rgba(251, 146, 60, 0.3)' : '1px solid rgba(251, 146, 60, 0.2)',
		boxShadow: '0 2px 8px rgba(251, 146, 60, 0.15)',
	},
	statusQrCode: {
		background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(29, 78, 216, 0.15) 100%)',
		color: theme.palette.type === 'dark' ? '#93c5fd' : '#1d4ed8',
		border: theme.palette.type === 'dark' ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
		boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
	},
	defaultChip: {
		background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
		color: theme.palette.type === 'dark' ? '#c4b5fd' : '#7c3aed',
		border: theme.palette.type === 'dark' ? '1px solid rgba(168, 85, 247, 0.3)' : '1px solid rgba(168, 85, 247, 0.2)',
		fontSize: '0.75rem',
		height: '26px',
		fontWeight: 600,
		borderRadius: '8px',
		boxShadow: '0 2px 8px rgba(168, 85, 247, 0.15)',
	},
	statusBadge: {
		background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(59,130,246,0.15) 100%)',
		color: '#c7d2fe',
		fontWeight: 700,
		border: '1px solid rgba(99,102,241,0.3)',
		fontSize: '0.75rem',
		height: '26px',
		borderRadius: '8px',
	},
	statusPill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(0.7, 1.2),
		borderRadius: 999,
		border: '1px solid rgba(148,163,184,0.4)',
		marginTop: theme.spacing(1),
	},
	statusDot: {
		width: 10,
		height: 10,
		borderRadius: '50%',
	},
	statusLabel: {
		fontWeight: 500,
		fontSize: '0.82rem',
		lineHeight: 1,
	},
	iconAction: {
		minWidth: 0,
		width: 38,
		height: 38,
		padding: 0,
		borderRadius: 12,
		border: '1px solid rgba(148, 163, 184, 0.35)',
		background: 'rgba(255,255,255,0.04)',
		color: '#e2e8f0',
		transition: 'all 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		"&:hover": {
			background: 'linear-gradient(135deg, rgba(99,102,241,0.3) 0%, rgba(59,130,246,0.3) 100%)',
			transform: 'translateY(-1px)',
		},
	},
	qrButton: {
		borderRadius: 10,
		background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
		color: '#fff',
		padding: theme.spacing(1.2, 1),
		fontWeight: 700,
	},
	metaApiChip: {
		background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%)',
		color: theme.palette.type === 'dark' ? '#67e8f9' : '#0891b2',
		border: theme.palette.type === 'dark' ? '1px solid rgba(14, 165, 233, 0.3)' : '1px solid rgba(14, 165, 233, 0.2)',
		fontSize: '0.75rem',
		height: '26px',
		fontWeight: 600,
		borderRadius: '8px',
		boxShadow: '0 2px 8px rgba(14, 165, 233, 0.15)',
	},
	whatsappWebChip: {
		background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
		color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
		border: theme.palette.type === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
		fontSize: '0.75rem',
		height: '26px',
		fontWeight: 600,
		borderRadius: '8px',
	},
	actionButtons: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1.2),
		marginTop: theme.spacing(1),
		paddingTop: theme.spacing(1),
		alignItems: 'center',
	},
	iconButton: {
		padding: theme.spacing(1),
		borderRadius: '10px',
		transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
		'&:hover': {
			transform: 'scale(1.1)',
		},
	},
		editButton: {
			color: '#5490ff',
			'&:hover': {
				backgroundColor: theme.palette.type === 'dark'
					? 'rgba(84, 144, 255, 0.18)'
					: 'rgba(84, 144, 255, 0.10)',
			},
		},
		deleteButton: {
			color: '#c72b40',
			'&:hover': {
				backgroundColor: theme.palette.type === 'dark'
					? 'rgba(199, 43, 64, 0.18)'
					: 'rgba(199, 43, 64, 0.10)',
			},
		},
	settingsButton: {
		color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
		'&:hover': {
			backgroundColor: theme.palette.type === 'dark'
				? 'rgba(255, 255, 255, 0.08)'
				: 'rgba(0, 0, 0, 0.04)',
		},
	},
	infoRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	emptyState: {
		textAlign: 'center',
		padding: theme.spacing(8),
		color: theme.palette.text.secondary,
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		padding: theme.spacing(4),
	},
}));

const Connections = () => {
	const classes = useStyles();
	const theme = useTheme();

	const { user } = useContext(AuthContext);
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [wahaActionStatus, setWahaActionStatus] = useState({});
	const [nowTick, setNowTick] = useState(Date.now());
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	// Controla feedback visual em ações WAHA
	const beginWahaAction = (whatsAppId, label) => {
		setWahaActionStatus(prev => ({
			...prev,
			[whatsAppId]: { label, startedAt: Date.now() }
		}));
	};

	const endWahaAction = (whatsAppId) => {
		setWahaActionStatus(prev => {
			const next = { ...prev };
			delete next[whatsAppId];
			return next;
		});
	};

	// Tick pra cronômetro em tempo real de ações WAHA (linha ~603/651).
	// Só roda quando há ação em curso — antes era always-on e re-renderizava
	// a página (lista de Cards, modal, toda a árvore) 1×/s mesmo ociosa,
	// puxando CPU pra 100% em máquinas modestas.
	const hasActiveWahaAction = Object.keys(wahaActionStatus).length > 0;
	React.useEffect(() => {
		if (!hasActiveWahaAction) return undefined;
		const timer = setInterval(() => setNowTick(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [hasActiveWahaAction]);

	// Novos handlers para WAHA
	const handleGenerateQR = async (whatsApp) => {
		beginWahaAction(whatsApp.id, "Iniciando sessão");
		try {
			await api.post(`/whatsapp/${whatsApp.id}/generate-qr`);
			toast.success("Sessão iniciada. Aguarde o QR Code.");
		} catch (err) {
			toastError(err);
		} finally {
			endWahaAction(whatsApp.id);
		}
	};

	const handleRestartSession = async (whatsApp) => {
		beginWahaAction(whatsApp.id, "Reiniciando");
		try {
			await api.post(`/whatsapp/${whatsApp.id}/restart`);
			toast.success("Sessão reiniciada. Aguarde atualização do status.");
		} catch (err) {
			toastError(err);
		} finally {
			endWahaAction(whatsApp.id);
		}
	};

	const handleReconnectMeta = async (whatsApp) => {
		beginWahaAction(whatsApp.id, "Reconectando");
		try {
			await api.post(`/whatsapp/${whatsApp.id}/reconnect`);
			toast.success("Reconexão iniciada. Aguarde atualização do status.");
		} catch (err) {
			toastError(err);
		} finally {
			endWahaAction(whatsApp.id);
		}
	};

	const handleDisconnectSession = async (whatsAppId) => {
		beginWahaAction(whatsAppId, "Parando sessão");
		try {
			await api.post(`/whatsapp/${whatsAppId}/disconnect`);
			toast.success("Sessão desconectada com sucesso!");
		} catch (err) {
			toastError(err);
		} finally {
			endWahaAction(whatsAppId);
		}
	};

	// Ações adicionais de sessão WAHA (GOWS) — Sessions API
	const handleStartSession = async (whatsApp) => {
		// Inicia/gera QR (POST /api/sessions/{name}/start → backend: /whatsapp/:id/generate-qr)
		await handleGenerateQR(whatsApp);
	};

	const handleStopSession = async (whatsApp) => {
		// Para/stop/logout (POST /api/sessions/{name}/stop → backend: /whatsapp/:id/disconnect)
		await handleDisconnectSession(whatsApp.id);
	};

	const handleRestartWahaSession = async (whatsApp) => {
		// Restart session (POST /api/sessions/{name}/restart → backend: /whatsapp/:id/restart)
		await handleRestartSession(whatsApp);
	};

	const handleOpenWhatsAppModal = () => {
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setSelectedWhatsApp, setQrModalOpen]);

	const handleEditWhatsApp = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.post(`/whatsapp/${confirmModalInfo.whatsAppId}/disconnect`);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				const target = whatsApps?.find(w => w.id === confirmModalInfo.whatsAppId);
				if (target?.provider === "waha") {
					beginWahaAction(confirmModalInfo.whatsAppId, "Apagando sessão");
					// Remove sessão no WAHA antes de apagar o card/conexão
					await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}/session`);
				}
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			} finally {
				endWahaAction(confirmModalInfo.whatsAppId);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
		setConfirmModalOpen(false);
	};

	const formatPhoneBR = (raw = "") => {
		const digits = String(raw).replace(/\D/g, "");
		if (!digits) return raw;

		const d = digits.startsWith("55") ? digits.slice(2) : digits;
		const ddd = d.slice(0, 2);
		const rest = d.slice(2);

		if (!ddd || !rest) return raw;

		// Brasil: celular = 9 dígitos após DDD (XXXXX-XXXX),
		// fixo    = 8 dígitos após DDD (XXXX-XXXX).
		if (rest.length === 9) {
			return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
		}
		if (rest.length === 8) {
			return `+55 ${ddd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
		}

		return raw;
	};

	const handleCopyNumber = useCallback(async (raw) => {
		const formatted = formatPhoneBR(raw);
		try {
			await navigator.clipboard.writeText(formatted);
			toast.success(`Número copiado: ${formatted}`);
		} catch {
			// Fallback para browsers antigos / contexto sem clipboard
			const ta = document.createElement("textarea");
			ta.value = formatted;
			ta.style.position = "fixed";
			ta.style.opacity = "0";
			document.body.appendChild(ta);
			ta.select();
			try {
				document.execCommand("copy");
				toast.success(`Número copiado: ${formatted}`);
			} catch {
				toast.error("Não foi possível copiar");
			}
			document.body.removeChild(ta);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getStatusInfo = (status) => {
		const normalized = (status || "").toUpperCase();

		// Traduções WAHA
		const wahaStatusMap = {
			'STOPPED': { label: 'Conexão Parada', color: '#94a3b8' },
			'STARTING': { label: 'INICIANDO...', color: '#f59e0b' },
			'SCAN_QR_CODE': { label: 'Leia o QR Code', color: '#3b82f6' },
			'WORKING': { label: 'Conectado', color: '#22c55e' },
			'FAILED': { label: 'Falha na Conexão', color: '#ef4444' }
		};

		if (wahaStatusMap[normalized]) return wahaStatusMap[normalized];

		const statusMap = {
			'CONNECTED': { label: 'Conectado', color: '#22c55e' },
			'DISCONNECTED': { label: 'Desconectado', color: '#ef4444' },
			'OPENING': { label: 'Conectando', color: '#f59e0b' },
			'QRCODE': { label: 'QR Code', color: '#3b82f6' },
			'qrcode': { label: 'QR Code', color: '#3b82f6' },
			'TIMEOUT': { label: 'Timeout', color: '#ef4444' },
			'PAIRING': { label: 'Pareando', color: '#0ea5e9' },
			'PENDING': { label: 'Pendente', color: '#0ea5e9' },
		};

		const statusInfo = statusMap[normalized] || statusMap[status] || { label: status, color: '#cbd5e1' };
		return statusInfo;
	};

	const renderActionButtons = whatsApp => {
		// Botões de sessão específicos para WAHA (GOWS)
		if (whatsApp.provider === "waha") {
			const wStatus = (whatsApp.status || "").toUpperCase();
			const isQr = wStatus === "SCAN_QR_CODE" || wStatus === "QRCODE" || whatsApp.status === "qrcode";
			const isOpening = wStatus === "STARTING" || wStatus === "OPENING";
			const isStopped = wStatus === "STOPPED" || wStatus === "DISCONNECTED";
			const showParar = !isStopped;
			const actionState = wahaActionStatus[whatsApp.id];
			const elapsed = actionState ? Math.floor((nowTick - actionState.startedAt) / 1000) : 0;

			return (
				<Box
					display="flex"
					flexDirection="column"
					width="100%"
					style={{ gap: theme.spacing(0.5) }}
				>
					<Box
						display="flex"
						flexWrap="nowrap"
						alignItems="center"
						style={{ gap: theme.spacing(1.4) }}
					>
						<Tooltip title={showParar ? "Parar" : "Iniciar"} arrow>
							<span>
								<Button
									size="small"
									variant={showParar ? "outlined" : "contained"}
									color={showParar ? "secondary" : "primary"}
									className={classes.iconAction}
									disabled={!!actionState}
									onClick={() => (showParar ? handleStopSession(whatsApp) : handleStartSession(whatsApp))}
								>
									{showParar ? <LucideStop size={16} /> : <LucidePlay size={16} />}
								</Button>
							</span>
						</Tooltip>

						<Tooltip title="Reiniciar" arrow>
							<span>
								<Button
									size="small"
									variant="outlined"
									color="default"
									className={classes.iconAction}
									disabled={!!actionState}
									onClick={() => handleRestartWahaSession(whatsApp)}
								>
									<LucideRotateCcw size={16} />
								</Button>
							</span>
						</Tooltip>
					</Box>

					{actionState && (
						<Chip
							label={`${actionState.label} (${elapsed}s)`}
							size="small"
							className={classes.statusChip}
						/>
					)}
					{isQr && (
						<Button
							size="small"
							variant="contained"
							color="primary"
							fullWidth
							onClick={() => handleOpenQrModal(whatsApp)}
						>
							Ver QR Code
						</Button>
					)}
					{isOpening && (
						<Button
							size="small"
							variant="outlined"
							disabled
							color="default"
							fullWidth
							startIcon={<CircularProgress size={16} />}
						>
							Conectando...
						</Button>
					)}
				</Box>
			);
		}

		// Meta-API: sem QR/restart. Apenas Reconectar (quando desconectado) ou
		// Conectando... (transição). O "Desconectar" não se aplica a Meta porque
		// não é uma sessão WhatsApp Web — é só HTTP + token.
		if (whatsApp.provider === "meta-api") {
			const status = whatsApp.status;
			if (status === "OPENING") {
				return (
					<Button
						size="small"
						variant="outlined"
						disabled
						color="default"
						fullWidth
						startIcon={<CircularProgress size={16} />}
					>
						Conectando...
					</Button>
				);
			}
			if (status === "CONNECTED") {
				// Conexão saudável — sem ações; o card já mostra status CONECTADO.
				return null;
			}
			// DISCONNECTED / TIMEOUT / FAILED / PENDING / default
			return (
				<Button
					size="small"
					variant="contained"
					color="primary"
					onClick={() => handleReconnectMeta(whatsApp)}
					fullWidth
				>
					Reconectar
				</Button>
			);
		}

		// Fluxo padrão (Baileys — obsoleto)
		return (
			<>
				{(whatsApp.status === "CONNECTED" || whatsApp.status === "PAIRING") && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => handleDisconnectSession(whatsApp.id)}
						fullWidth
					>
						Desconectar
					</Button>
				)}

				{whatsApp.status === "qrcode" && (
					<Box
						display="flex"
						width="100%"
						style={{ gap: theme.spacing(1), flexWrap: "wrap", minWidth: 0 }}
					>
						<Button
							size="small"
							variant="contained"
							color="primary"
							onClick={() => handleOpenQrModal(whatsApp)}
							style={{ flex: 1, minWidth: 140 }}
						>
							Ver QR Code
						</Button>
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRestartSession(whatsApp)}
							style={{ flex: 1, minWidth: 140 }}
						>
							Reiniciar
						</Button>
					</Box>
				)}

				{(whatsApp.status === "DISCONNECTED" || whatsApp.status === "TIMEOUT") && (
					<Box
						display="flex"
						width="100%"
						style={{ gap: 5, flexWrap: "wrap", minWidth: 0 }}
					>
						<Button
							size="small"
							variant="contained"
							color="primary"
							onClick={() => handleGenerateQR(whatsApp)}
							style={{ flex: 1, minWidth: 140 }}
						>
							Gerar QR Code
						</Button>
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRestartSession(whatsApp)}
							style={{ flex: 1, minWidth: 140 }}
						>
							Reiniciar
						</Button>
					</Box>
				)}

				{whatsApp.status === "OPENING" && (
					<Button
						size="small"
						variant="outlined"
						disabled
						color="default"
						fullWidth
						startIcon={<CircularProgress size={16} />}
					>
						Conectando...
					</Button>
				)}
				{whatsApp.status === "PENDING" && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
						fullWidth
					>
						{i18n.t("connections.buttons.disconnect")}
					</Button>
				)}
			</>
		);
	};

	return (
		<MainContainer className={classes.root}>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<div className={classes.header}>
				<Typography className={classes.title}>
					{i18n.t("connections.title")}
				</Typography>
				<div className={classes.buttonsWrapper}>
					<Can
						role={user.profile}
						perform="connections-page:addConnection"
						yes={() => (
							<Button
								variant="contained"
								color="primary"
								onClick={handleOpenWhatsAppModal}
								data-onboarding-tour="connectWhatsapp"
								className={classes.addButton}
								startIcon={<Plus size={15} strokeWidth={1.75} />}
							>
								{i18n.t("connections.buttons.add")}
							</Button>
						)}
					/>
				</div>
			</div>
			<Paper className={classes.mainPaper}>
				{loading ? (
					<div className={classes.loadingContainer}>
						<CircularProgress />
					</div>
				) : (
					<>
						{whatsApps?.length === 0 ? (
							<div className={classes.emptyState}>
								<Typography variant="h6" gutterBottom>
									Nenhuma conexão encontrada
								</Typography>
								<Typography variant="body2">
									Clique em "Adicionar" para criar uma nova conexão
								</Typography>
							</div>
						) : (
							<div className={classes.cardsContainer}>
								{whatsApps.map(whatsApp => (
									<Card key={whatsApp.id} className={classes.card}>
										<CardContent>
											<div className={classes.cardHeader}>
												<div style={{ flex: 1 }}>
													<Box
														display="flex"
														alignItems="center"
														mb={0.5}
														style={{ gap: theme.spacing(1.6) }}
													>
														<Tooltip title={whatsApp.provider === "meta-api" ? "WhatsApp Business API" : "WhatsApp Web"} arrow>
															<span style={{ display: 'flex', alignItems: 'center' }}>
																{whatsApp.provider === "meta-api" ? (
																	<FaWhatsappSquare color="#25D366" size={24} />
																) : (
																	<FaWhatsapp color="#25D366" size={24} />
																)}
															</span>
														</Tooltip>
														<Typography className={classes.connectionName}>
															{whatsApp.name}
														</Typography>
													</Box>
													{(() => {
														const info = getStatusInfo(whatsApp.status);
														return (
															<Box
																className={classes.statusPill}
																style={{
																	borderColor: `${info.color}55`,
																	background: `${info.color}14`,
																}}
															>
																<span
																	className={classes.statusDot}
																	style={{ background: info.color }}
																/>
																<span
																	className={classes.statusLabel}
																	style={{ color: info.color }}
																>
																	{info.label}
																</span>
															</Box>
														);
													})()}
													{whatsApp.number && (
														<Box style={{ marginTop: theme.spacing(1.2) }}>
															<Tooltip
																title="Clique para copiar"
																placement="top"
																arrow
															>
																<Chip
																	label={formatPhoneBR(whatsApp.number)}
																	size="small"
																	clickable
																	onClick={() => handleCopyNumber(whatsApp.number)}
																	style={{
																		background: theme.palette.type === "dark"
																			? "rgba(255,255,255,0.06)"
																			: "rgba(15,23,42,0.06)",
																		border: theme.palette.type === "dark"
																			? "1px solid rgba(148,163,184,0.22)"
																			: "1px solid rgba(148,163,184,0.35)",
																		color: theme.palette.type === "dark"
																			? "rgba(255,255,255,0.72)"
																			: "rgba(15,23,42,0.65)",
																		fontWeight: 500,
																		letterSpacing: "0.6px",
																		borderRadius: 999,
																		height: 24,
																	}}
																/>
															</Tooltip>
														</Box>
													)}
												</div>
												<Can
													role={user.profile}
													perform="connections-page:editOrDeleteConnection"
													yes={() => (
														<div className={classes.cardActions}>
																<IconButton
																	size="small"
																	className={`${classes.iconButton} ${classes.editButton}`}
																	onClick={() => handleEditWhatsApp(whatsApp)}
																	title="Editar"
																>
																	<Pencil size={18} strokeWidth={1.5} color="#5490ff" />
																</IconButton>
																<IconButton
																	size="small"
																	className={`${classes.iconButton} ${classes.deleteButton}`}
																	onClick={() => handleOpenConfirmationModal("delete", whatsApp.id)}
																	title="Deletar"
																>
																	<Trash size={18} strokeWidth={1.5} color="#c72b40" />
																</IconButton>
															</div>
														)}
													/>
											</div>

											<Can
												role={user.profile}
												perform="connections-page:actionButtons"
												yes={() => (
													<div className={classes.actionButtons}>
														{renderActionButtons(whatsApp)}
													</div>
												)}
											/>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Connections;
